import Quill from "quill";

/**
 * HTML source view for the Quill editors.
 *
 * The toolbar button is appended dynamically (instead of being part of the
 * toolbar config) so the editor does not have to be re-created when the role
 * of the logged in user arrives from the API.
 *
 * Once the source has been edited the markup is written to the editor DOM
 * verbatim and Quill's MutationObserver is detached, so nothing re-parses the
 * content: whatever is typed here is exactly what gets saved. Quill cannot
 * represent arbitrary markup (iframes, tables, inline styles), and letting it
 * re-read the DOM would silently rewrite the content. In exchange the visual
 * editor is switched to read-only, because its internal model no longer
 * matches the DOM.
 */

const SOURCE_ICON =
  '<svg viewBox="0 0 18 18">' +
  '<polyline class="ql-even ql-stroke" points="6.5 5 3 9 6.5 13"></polyline>' +
  '<polyline class="ql-even ql-stroke" points="11.5 5 15 9 11.5 13"></polyline>' +
  '<line class="ql-stroke" x1="10.5" x2="7.5" y1="4" y2="14"></line>' +
  "</svg>";

const VOID_TAGS = /^<(br|hr|img|input|meta|link|source|col|area|embed)\b/i;
const SELF_CLOSING = /^<[^>]+\/>$/;
const OPEN_TAG = /^<[a-z][a-z0-9-]*/i;
const CLOSE_TAG = /^<\//;
const PAIR_ON_ONE_LINE = /^<([a-z][a-z0-9-]*)\b[^>]*>[\s\S]*<\/\1>$/i;
const SPLIT = "\u0000";

/**
 * Puts every tag boundary on its own line and indents by nesting depth.
 *
 * Line breaks and indentation are only ever inserted between a ">" and a "<",
 * never inside text, so compactHtml() below is an exact inverse.
 */
export const prettyHtml = (html) => {
  let depth = 0;
  // A tördelés saját határjelön megy, nem "\n"-en: a szövegben eleve ott lévő
  // sortöréseket nem szabad tördelési pontnak venni, mert az így kapott
  // behúzást a compactHtml() már nem tudná visszavonni.
  return (html ?? "")
    .replace(/>\s*</g, `>${SPLIT}<`)
    .split(SPLIT)
    .map((line) => {
      const trimmed = line.trim();
      if (CLOSE_TAG.test(trimmed)) {
        depth = Math.max(0, depth - 1);
      }
      const indented = "  ".repeat(depth) + trimmed;
      if (
        OPEN_TAG.test(trimmed) &&
        !CLOSE_TAG.test(trimmed) &&
        !VOID_TAGS.test(trimmed) &&
        !SELF_CLOSING.test(trimmed) &&
        !PAIR_ON_ONE_LINE.test(trimmed)
      ) {
        depth += 1;
      }
      return indented;
    })
    .join("\n")
    .trim();
};

/** Removes the formatting added by prettyHtml(), leaving text untouched. */
export const compactHtml = (html) =>
  /<pre[\s>]/i.test(html ?? "")
    ? (html ?? "").trim()
    : (html ?? "").replace(/>\s*\n\s*</g, "><").trim();

/**
 * Adds the "HTML forrás" button to the toolbar of an existing Quill instance
 * and wires the editor <-> textarea switching.
 *
 * @param {Quill} quill        the live editor instance
 * @param {Function} onApply   called with the quill instance after the edited
 *                             source has been written back
 * @returns {{toggle: Function, isActive: Function, destroy: Function}}
 */
export const attachQuillSource = (quill, onApply) => {
  const toolbar = quill.getModule("toolbar");
  if (!toolbar || !toolbar.container) {
    return { toggle: () => {}, isActive: () => false, destroy: () => {} };
  }

  const textarea = document.createElement("textarea");
  textarea.className = "ql-source-editor";
  textarea.spellcheck = false;
  textarea.style.display = "none";

  const notice = document.createElement("div");
  notice.className = "ql-source-notice";
  notice.textContent =
    "Nyers HTML mód: a tartalom pontosan így kerül mentésre. " +
    "A vizuális szerkesztés az űrlap újratöltéséig szünetel.";
  notice.style.display = "none";

  quill.container.appendChild(textarea);
  quill.container.appendChild(notice);

  let active = false;
  let raw = false;

  const enter = () => {
    textarea.value = prettyHtml(quill.root.innerHTML);
    textarea.style.height = `${Math.max(quill.root.offsetHeight, 180)}px`;
    quill.root.style.display = "none";
    textarea.style.display = "block";
    toolbar.container.classList.add("ql-source-on");
    active = true;
    textarea.focus();
  };

  const apply = (html) => {
    // Detach the MutationObserver first: Quill would otherwise re-read the
    // DOM and rebuild it from its own model, which drops everything it has
    // no blot for (iframes, tables) and turns the whitespace between block
    // elements into empty paragraphs.
    quill.scroll.observer.disconnect();
    quill.root.innerHTML = html;

    if (!raw) {
      raw = true;
      quill.enable(false);
      quill.container.classList.add("ql-source-raw");
      toolbar.container.classList.add("ql-source-raw");
      notice.style.display = "block";
    }
    if (typeof onApply === "function") {
      onApply(quill);
    }
  };

  const leave = () => {
    const html = compactHtml(textarea.value);
    textarea.style.display = "none";
    quill.root.style.display = "";
    toolbar.container.classList.remove("ql-source-on");
    active = false;

    if (html !== quill.root.innerHTML.trim()) {
      apply(html);
    } else if (!raw) {
      quill.focus();
    }
  };

  const toggle = () => (active ? leave() : enter());

  const group = document.createElement("span");
  group.className = "ql-formats ql-source-formats";
  const button = document.createElement("button");
  button.type = "button";
  button.className = "ql-source";
  button.title = "HTML forrás szerkesztése";
  button.innerHTML = SOURCE_ICON;
  group.appendChild(button);
  toolbar.container.appendChild(group);

  toolbar.addHandler("source", toggle);
  toolbar.attach(button);

  return {
    toggle,
    isActive: () => active,
    isRaw: () => raw,
    destroy: () => {
      if (active) {
        quill.root.style.display = "";
        toolbar.container.classList.remove("ql-source-on");
        active = false;
      }
      group.remove();
      textarea.remove();
      notice.remove();
    },
  };
};
